import { Box, Button, Flex, IconButton, SimpleGrid, Text, useToast } from '@chakra-ui/react'
import React, { useCallback, useRef, useState } from 'react'
import { FiRefreshCw, FiSquare, FiTrash2, FiX } from 'react-icons/fi'
import { ChatMessage, UserKnowledge } from '../types'
import { StreamPhase, useChatApi } from '../useChatApi'
import ChatInput from './ChatInput'
import ChatMessageBubble from './ChatMessageBubble'
import TypingIndicator from './TypingIndicator'

interface ChatPanelProps {
  messages: ChatMessage[]
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  onClose?: () => void
  showCloseButton?: boolean
  userKnowledge?: Partial<UserKnowledge>
  height?: string
}

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    "Hi! I'm JamiiPesa AI, your financial education assistant. I can help you understand investing, explain financial concepts, look up stock prices, and answer questions about JamiiPesa. What would you like to learn about?",
  timestamp: new Date().toISOString()
}

const STARTER_QUESTIONS = [
  'Why should I save money?',
  'What is investing?',
  'How do I build generational wealth?',
  'What is JamiiPesa?'
]

const ALPHA_DISCLAIMER =
  "This is an educational tool in alpha and may make mistakes. It's not a replacement for professional financial advice. Always consult with a licensed financial advisor and conduct your own research before making investment decisions."

const PHASE_LABELS: Record<StreamPhase, string> = {
  idle: '',
  thinking: 'Thinking...',
  searching: 'Searching financial data...',
  generating: ''
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  setMessages,
  onClose,
  showCloseButton = true,
  userKnowledge,
  height = '500px'
}) => {
  const { sendMessageStreaming, isLoading, phase, error, abort } = useChatApi()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const toast = useToast()
  const [streamingText, setStreamingText] = useState('')
  const streamingTextRef = useRef('')
  const [failedMessageId, setFailedMessageId] = useState<string | null>(null)

  // const scrollToBottom = useCallback(() => {
  //   messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
  // }, [])

  // useEffect(() => {
  //   scrollToBottom()
  // }, [messages, isLoading, streamingText, scrollToBottom])

  const handleSend = useCallback(
    async (content: string) => {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date().toISOString()
      }

      setMessages((prev) => [...prev, userMessage])
      setFailedMessageId(null)
      setStreamingText('')
      streamingTextRef.current = ''

      try {
        const response = await sendMessageStreaming(
          content,
          messages,
          (delta) => {
            streamingTextRef.current += delta
            setStreamingText(streamingTextRef.current)
          },
          undefined,
          userKnowledge
        )

        setStreamingText('')
        streamingTextRef.current = ''

        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.message,
          timestamp: new Date().toISOString(),
          tickers: response.tickers,
          companies: response.companies,
          summary: response.summary
        }

        setMessages((prev) => [...prev, assistantMessage])
      } catch (err: any) {
        setStreamingText('')
        streamingTextRef.current = ''

        if (err.message !== 'Request was cancelled') {
          setFailedMessageId(userMessage.id)
          toast({
            title: 'Error',
            description: err.message || 'Failed to send message',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right'
          })
        }
      }
    },
    [messages, sendMessageStreaming, setMessages, toast, userKnowledge]
  )

  const handleRetry = useCallback(() => {
    if (!failedMessageId) return
    const failedMsg = messages.find((m) => m.id === failedMessageId)
    if (!failedMsg) return
    // Remove the failed user message, then re-send
    setMessages((prev) => prev.filter((m) => m.id !== failedMessageId))
    setFailedMessageId(null)
    handleSend(failedMsg.content)
  }, [failedMessageId, messages, setMessages, handleSend])

  const handleClear = useCallback(() => {
    setMessages([])
  }, [setMessages])

  const displayMessages = messages.length === 0 ? [WELCOME_MESSAGE] : messages

  return (
    <Flex direction="column" h={height} bg="white" borderRadius="lg" overflow="hidden">
      {/* Header */}
      <Flex
        px={4}
        py={3}
        bg="brand.500"
        color="white"
        align="center"
        justify="space-between"
        flexShrink={0}
      >
        <Flex align="center" gap={2}>
          <Box w="8px" h="8px" borderRadius="full" bg="green.300" />
          <Text fontWeight="bold" fontSize="sm">
            JamiiPesa AI
          </Text>
          {phase !== 'idle' && phase !== 'generating' && (
            <Text fontSize="xs" color="whiteAlpha.800" fontStyle="italic">
              {PHASE_LABELS[phase]}
            </Text>
          )}
        </Flex>
        <Flex gap={1}>
          {isLoading && (
            <IconButton
              aria-label="Stop generating"
              icon={<FiSquare />}
              size="xs"
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              onClick={abort}
            />
          )}
          {messages.length > 0 && !isLoading && (
            <IconButton
              aria-label="Clear chat"
              icon={<FiTrash2 />}
              size="xs"
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              onClick={handleClear}
            />
          )}
          {showCloseButton && onClose && (
            <IconButton
              aria-label="Close chat"
              icon={<FiX />}
              size="xs"
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              onClick={onClose}
            />
          )}
        </Flex>
      </Flex>

      {/* Messages area */}
      <Box flex={1} overflowY="auto" py={3} bg="gray.50">
        {displayMessages.map((msg) => (
          <React.Fragment key={msg.id}>
            <ChatMessageBubble message={msg} />
            {msg.id === failedMessageId && (
              <Flex justify="flex-end" px={2} mt={-1} mb={2}>
                <Flex
                  as="button"
                  align="center"
                  gap={1}
                  fontSize="xs"
                  color="red.500"
                  _hover={{ color: 'red.700' }}
                  onClick={handleRetry}
                  cursor="pointer"
                  bg="transparent"
                  border="none"
                  p={0}
                >
                  <FiRefreshCw size={12} />
                  <Text fontSize="xs">Failed to send. Retry</Text>
                </Flex>
              </Flex>
            )}
          </React.Fragment>
        ))}
        {/* Streaming text — shown as a temporary bubble while generating */}
        {streamingText && (
          <ChatMessageBubble
            message={{
              id: 'streaming',
              role: 'assistant',
              content: streamingText,
              timestamp: new Date().toISOString()
            }}
          />
        )}
        {/* Starter questions — shown only on the welcome screen */}
        {messages.length === 0 && !isLoading && (
          <Box px={4} pb={2}>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
              {STARTER_QUESTIONS.map((q) => (
                <Button
                  key={q}
                  size="sm"
                  variant="outline"
                  borderColor="gray.200"
                  color="gray.700"
                  fontWeight="medium"
                  fontSize="xs"
                  whiteSpace="normal"
                  textAlign="left"
                  height="auto"
                  py={2}
                  px={3}
                  borderRadius="lg"
                  _hover={{ borderColor: 'brand.400', color: 'brand.600', bg: 'brand.50' }}
                  onClick={() => handleSend(q)}
                >
                  {q}
                </Button>
              ))}
            </SimpleGrid>
          </Box>
        )}
        {isLoading && !streamingText && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </Box>

      {/* Error banner */}
      {error && (
        <Box px={4} py={2} bg="red.50" borderTop="1px solid" borderColor="red.200">
          <Text fontSize="xs" color="red.600">
            {error}
          </Text>
        </Box>
      )}

      {/* Input */}
      <ChatInput onSend={handleSend} isLoading={isLoading} />

      {/* Alpha disclaimer */}
      <Box px={4} py={2} bg="gray.50" borderTop="1px solid" borderColor="gray.100" flexShrink={0}>
        <Text fontSize="xs" color="gray.400" textAlign="center" lineHeight="1.4">
          {ALPHA_DISCLAIMER}
        </Text>
      </Box>
    </Flex>
  )
}

export default ChatPanel
